import { useMemo, useState } from "react";
import { getBackendBaseUrl } from "../../shared/api/backendDiscovery";
import "./styles/index.css";

const safeJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const buildResult = ({ id, label, method, endpoint, ok, status, durationMs, details }) => ({
  id,
  label,
  method,
  endpoint,
  ok,
  status,
  durationMs,
  details,
});

export default function BackendDiagnosticsPage() {
  const [backendUrl, setBackendUrl] = useState("");
  const [detectedPort, setDetectedPort] = useState("-");
  const [running, setRunning] = useState(false);
  const [lastRunAt, setLastRunAt] = useState(null);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);

  const totals = useMemo(() => {
    const total = results.length;
    const passed = results.filter((item) => item.ok).length;
    const failed = total - passed;
    return { total, passed, failed };
  }, [results]);

  const runDiagnostics = async () => {
    setRunning(true);
    setError("");
    setResults([]);

    try {
      const baseUrl = await getBackendBaseUrl();
      setBackendUrl(baseUrl);

      const portMatch = baseUrl.match(/:(\d+)(?:\/|$)/);
      setDetectedPort(portMatch?.[1] || "prod");

      const output = [];

      const healthStart = performance.now();
      const healthResponse = await fetch(`${baseUrl}/api/health`);
      const healthJson = await safeJson(healthResponse);
      const healthDuration = Math.round(performance.now() - healthStart);
      const healthOk = healthResponse.ok && healthJson?.service === "andre-backend";

      output.push(
        buildResult({
          id: "health",
          label: "Healthcheck",
          method: "GET",
          endpoint: "/api/health",
          ok: healthOk,
          status: healthResponse.status,
          durationMs: healthDuration,
          details: healthJson || "Sem JSON",
        })
      );

      const typenameStart = performance.now();
      const typenameResponse = await fetch(`${baseUrl}/graphql`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: "query { __typename }" }),
      });
      const typenameJson = await safeJson(typenameResponse);
      const typenameDuration = Math.round(performance.now() - typenameStart);
      const typenameOk = typenameResponse.ok && typenameJson?.data?.__typename === "Query";

      output.push(
        buildResult({
          id: "graphql-typename",
          label: "GraphQL Root",
          method: "POST",
          endpoint: "/graphql",
          ok: typenameOk,
          status: typenameResponse.status,
          durationMs: typenameDuration,
          details: typenameJson || "Sem JSON",
        })
      );

      const playlistStart = performance.now();
      const playlistResponse = await fetch(`${baseUrl}/graphql`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: "query { playlist { title artist url } }",
        }),
      });
      const playlistJson = await safeJson(playlistResponse);
      const playlistDuration = Math.round(performance.now() - playlistStart);
      const tracks = playlistJson?.data?.playlist || [];
      const playlistOk = playlistResponse.ok && Array.isArray(tracks);

      output.push(
        buildResult({
          id: "graphql-playlist",
          label: "GraphQL Playlist",
          method: "POST",
          endpoint: "/graphql",
          ok: playlistOk,
          status: playlistResponse.status,
          durationMs: playlistDuration,
          details: {
            tracksFound: tracks.length,
            firstTrack: tracks[0] || null,
          },
        })
      );

      const emailStart = performance.now();
      const emailResponse = await fetch(`${baseUrl}/api/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const emailJson = await safeJson(emailResponse);
      const emailDuration = Math.round(performance.now() - emailStart);
      const emailOk = emailResponse.status === 400;

      output.push(
        buildResult({
          id: "email-validation",
          label: "Email Validation",
          method: "POST",
          endpoint: "/api/send-email",
          ok: emailOk,
          status: emailResponse.status,
          durationMs: emailDuration,
          details: emailJson || "Sem JSON",
        })
      );

      if (tracks.length > 0 && tracks[0].url) {
        const assetStart = performance.now();
        const assetResponse = await fetch(tracks[0].url, { method: "HEAD" });
        const assetDuration = Math.round(performance.now() - assetStart);
        const assetOk = assetResponse.ok;

        output.push(
          buildResult({
            id: "asset-track",
            label: "MP3 Asset",
            method: "HEAD",
            endpoint: tracks[0].url.replace(baseUrl, ""),
            ok: assetOk,
            status: assetResponse.status,
            durationMs: assetDuration,
            details: { url: tracks[0].url },
          })
        );
      } else {
        output.push(
          buildResult({
            id: "asset-track-skipped",
            label: "MP3 Asset",
            method: "HEAD",
            endpoint: "/assets/mp3/<track>",
            ok: true,
            status: "SKIP",
            durationMs: 0,
            details: "Nenhuma faixa retornada pela playlist para testar asset.",
          })
        );
      }

      setResults(output);
      setLastRunAt(new Date());
    } catch (runError) {
      setError(runError?.message || "Falha ao executar diagnostico.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <main className="backendDiagPage">
      <section className="backendDiagShell">
        <header className="backendDiagHeader">
          <p className="backendDiagKicker">andremourapassos://backend-check</p>
          <h1>Painel de Diagnostico do Backend</h1>
          <p className="backendDiagSubtitle">
            Teste visual de porta dinamica, saude da API, GraphQL, envio de email (validacao) e
            assets MP3.
          </p>
        </header>

        <section className="backendDiagMeta">
          <article>
            <p className="label">Backend URL</p>
            <strong>{backendUrl || "Aguardando execucao..."}</strong>
          </article>
          <article>
            <p className="label">Porta Detectada</p>
            <strong>{detectedPort}</strong>
          </article>
          <article>
            <p className="label">Ultima Execucao</p>
            <strong>{lastRunAt ? lastRunAt.toLocaleString() : "-"}</strong>
          </article>
          <article>
            <p className="label">Resumo</p>
            <strong>
              {totals.passed}/{totals.total} OK
            </strong>
          </article>
        </section>

        <div className="backendDiagActions">
          <button type="button" onClick={runDiagnostics} disabled={running}>
            {running ? "Executando..." : "Executar Diagnostico"}
          </button>
        </div>

        {error && <p className="backendDiagError">{error}</p>}

        <section className="backendDiagTableWrap">
          <table className="backendDiagTable">
            <thead>
              <tr>
                <th>Recurso</th>
                <th>Metodo</th>
                <th>Endpoint</th>
                <th>Status</th>
                <th>Tempo</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {results.map((item) => (
                <tr key={item.id}>
                  <td>{item.label}</td>
                  <td>{item.method}</td>
                  <td>{item.endpoint}</td>
                  <td>{String(item.status)}</td>
                  <td>{item.durationMs}ms</td>
                  <td>
                    <span className={item.ok ? "tagOk" : "tagFail"}>{item.ok ? "OK" : "FALHA"}</span>
                  </td>
                </tr>
              ))}
              {results.length === 0 && (
                <tr>
                  <td colSpan="6" className="backendDiagEmpty">
                    Clique em "Executar Diagnostico" para validar os recursos do backend.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        {results.length > 0 && (
          <section className="backendDiagDetails">
            <h2>Detalhes</h2>
            {results.map((item) => (
              <article key={`${item.id}-details`}>
                <h3>
                  {item.label} ({item.method} {item.endpoint})
                </h3>
                <pre>{JSON.stringify(item.details, null, 2)}</pre>
              </article>
            ))}
          </section>
        )}
      </section>
    </main>
  );
}
